import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { z } from 'zod';
import { associationLabels, en, fr, type Messages } from './messages';

export type Locale = 'fr' | 'en';

const STORAGE_KEY = 'syndiqia.locale';
const dictionaries: Record<Locale, Messages> = { fr, en };

function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'fr' || saved === 'en') return saved;
  } catch {
    // storage unavailable (private mode): fall back to French
  }
  return 'fr';
}

interface I18n {
  locale: Locale;
  t: Messages;
  setLocale(locale: Locale): void;
  /** Quebec formatting for numbers and dates in the current language. */
  number(value: number, fractionDigits?: number): string;
  /** Amount in cents, formatted as Canadian dollars. */
  money(cents: number): string;
  date(isoDate: string): string;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    // Validation messages from the shared zod contracts follow the UI language.
    z.config(locale === 'fr' ? z.locales.fr() : z.locales.en());
  }, [locale]);

  const value = useMemo<I18n>(() => {
    const tag = locale === 'fr' ? 'fr-CA' : 'en-CA';
    return {
      locale,
      t: dictionaries[locale],
      setLocale(next) {
        setLocaleState(next);
        try {
          localStorage.setItem(STORAGE_KEY, next);
        } catch {
          // not persisted; fine
        }
      },
      number: (v, digits = 0) =>
        new Intl.NumberFormat(tag, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v),
      money: (cents) => new Intl.NumberFormat(tag, { style: 'currency', currency: 'CAD' }).format(cents / 100),
      date: (iso) => {
        const text = new Intl.DateTimeFormat(tag, { dateStyle: 'long', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`));
        return locale === 'fr' ? text.replace(/^1 /, '1er ') : text; // « 1er avril », as written in Quebec
      },
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n outside I18nProvider');
  return ctx;
}

/** Inside an owners' association, the labels speak of members and the association (see associationLabels). */
export function StructureLabels({ structure, children }: { structure: 'divided_coownership' | 'npo_with_servitudes'; children: ReactNode }) {
  const parent = useI18n();
  const value = useMemo(() => (structure === 'npo_with_servitudes' ? { ...parent, t: associationLabels[parent.locale](parent.t) } : parent), [parent, structure]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
